
import { useState, useEffect } from "react";
import { useStore } from "@/store/useStore";
import { Dealer } from "@/types/dealer";
import { useGeolocation } from "@/hooks/useGeolocation";
import { fetchDealersData } from "./dealersService";
import { filterDealers, sortDealers } from "./dealersFilters";
import { dealersCache } from "./useDealersCache";
import { DealerFilterState, UseDealersResult } from "./types";
import { invalidateAllCaches } from "@/lib/cacheInvalidation";

export type { DealerFilterState } from "./types";
export { DEALERS_CACHE_KEY } from "./useDealersCache";

export const useDealers = (): UseDealersResult => {
  const { fetchDealers: fetchDealersFromStore } = useStore();
  const [isLoading, setIsLoading] = useState(true);
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const { coordinates } = useGeolocation({
    showSuccessToast: false
  });
  const [filters, setFilters] = useState<DealerFilterState>({
    search: "",
    range: [0, 5],
    filterVerified: false,
    filterActivated: false,
    sortOrder: "distance",
    distanceRange: 0, // Default to 0 (All)
  });
  const [lastFetch, setLastFetch] = useState<number>(0);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Verifica se i dati sono in cache e non sono scaduti
        const cachedDealers = dealersCache.getDealers();
        
        if (cachedDealers) {
          console.log("Caricamento dealer dalla cache, ultima fetch: ", new Date(lastFetch).toLocaleTimeString());
          setDealers(cachedDealers);
          setIsLoading(false);
          return;
        }
        
        console.log("Cache scaduta o non presente, caricamento dealer dal database");
        
        // Fetch dealers from store (for favorites, etc.)
        await fetchDealersFromStore();
        
        // Load dealers from the database
        const formattedDealers = await fetchDealersData();
        
        // Salva i dati in cache
        dealersCache.setDealers(formattedDealers);
        setLastFetch(Date.now());
        
        setDealers(formattedDealers);
      } catch (error) {
        console.error("Error in useDealers:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [fetchDealersFromStore]);

  const updateFilters = (newFilters: Partial<DealerFilterState>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const refreshDealers = async () => {
    setIsLoading(true);
    
    // Invalidate all dealer caches using the central utility
    invalidateAllCaches();
    
    await fetchDealersFromStore();
    
    try {
      // Load dealers from the database
      const formattedDealers = await fetchDealersData();
      
      // Aggiorna la cache con i nuovi dati
      dealersCache.setDealers(formattedDealers);
      setLastFetch(Date.now());
      
      setDealers(formattedDealers);
    } catch (error) {
      console.error("Error during refresh:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Apply filters and sorting
  const filteredDealers = filterDealers(dealers, filters, coordinates);
  const sortedDealers = sortDealers(filteredDealers, filters.sortOrder, coordinates);

  return {
    dealers: sortedDealers,
    isLoading,
    filters,
    updateFilters,
    refreshDealers,
  };
};
